import { useState } from "react";
import { Copy, Mail, Check, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

interface ReferralShareButtonsProps {
  referralUrl: string;
  onEmailClick: () => void;
  compact?: boolean;
}

export function ReferralShareButtons({ referralUrl, onEmailClick, compact }: ReferralShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    toast({ title: "Link copied to clipboard" });
    setTimeout(() => setCopied(false), 2000);
  };

  const SHORT_SHARE_COPY = `We switched to Nursly and it's taken away the panic of an unannounced Ofsted visit. Everything's ready in one click, ratios watched live, certificates flagged before they run out. Two months off if you sign up with my link: ${referralUrl}`;

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(SHORT_SHARE_COPY)}`, "_blank");
  };

  const handleCopyMessage = async () => {
    await navigator.clipboard.writeText(SHORT_SHARE_COPY);
    toast({ title: "Message copied to clipboard" });
  };

  return (
    <div className="space-y-3">
      {/* URL input with copy */}
      <div className="flex gap-2">
        <Input
          readOnly
          value={referralUrl}
          className="bg-nursly-cream text-sm font-body"
          onClick={(e) => (e.target as HTMLInputElement).select()}
        />
        <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0 gap-1.5">
          {copied ? <><Check className="h-3.5 w-3.5 text-nursly-sage" /> Copied!</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
        </Button>
      </div>

      {/* Share buttons */}
      <div className={`grid gap-2 ${compact ? "grid-cols-3" : "grid-cols-3"}`}>
        <button
          onClick={onEmailClick}
          className="flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-body font-medium transition-colors hover:bg-nursly-cream"
          style={{ borderColor: "rgba(13,31,20,0.09)" }}
        >
          <Mail className="h-4 w-4 text-nursly-sage" />
          <span className="text-nursly-ink">Email</span>
        </button>

        <button
          onClick={handleWhatsApp}
          className="flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-body font-medium transition-colors hover:bg-nursly-cream"
          style={{ borderColor: "rgba(13,31,20,0.09)" }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span className="text-nursly-ink">WhatsApp</span>
        </button>

        <button
          onClick={handleCopyMessage}
          className="flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-body font-medium transition-colors hover:bg-nursly-cream"
          style={{ borderColor: "rgba(13,31,20,0.09)" }}
        >
          <Link2 className="h-4 w-4 text-nursly-sage" />
          <span className="text-nursly-ink">Copy message</span>
        </button>
      </div>
    </div>
  );
}
